<script setup lang="ts">
import type { ChatMessageSendingJobForm_Message } from '../types'
import ChatMessageSendingJobNewPageImageInput from './ChatMessageSendingJobNewPageImageInput.vue'
import ChatMessageSendingJobNewPageTextInput from './ChatMessageSendingJobNewPageTextInput.vue'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import IconClose from '@/components/icons/IconClose.vue'
import IconAngle from '@/components/icons/IconAngle.vue'
import { nanoid } from 'nanoid'

const message = defineModel<ChatMessageSendingJobForm_Message>({
  required: true,
})

const props = defineProps<{
  shownControl: boolean
}>()

const emit = defineEmits<{
  (event: 'movePrevious'): void
  (event: 'moveNext'): void
  (event: 'delete'): void
}>()

const messageOptions = [
  {
    type: 'text',
    label: 'テキスト',
    handler: () => {
      message.value = {
        id: nanoid(),
        type: 'text',
        text: '',
        selections: undefined,
      }
    },
  },
  {
    type: 'image',
    label: '画像',
    handler: () => {
      message.value = {
        id: nanoid(),
        type: 'image',
        image: undefined,
        altText: '',
        linkUri: '',
      }
    },
  },
] as const

const clickDeleteButton = () => {
  const confirmed = (() => {
    if (message.value.type === 'text' && message.value.text !== '') {
      const confirmed = window.confirm('削除しますか？')
      if (!confirmed) {
        return false
      }
    }

    return true
  })()

  if (confirmed) {
    emit('delete')
  }
}
</script>

<template>
  <section>
    <header :class="$style.header">
      <div :class="$style.optionsWrapper">
        <label
          v-for="option of messageOptions"
          :key="option.type"
          :class="$style.option"
        >
          <DolphinRadioButton
            name="message"
            :value="option.type"
            :modelValue="message.type"
            @update:modelValue="option.handler"
          />
          <span :class="$style.option__text">
            {{ option.label }}
          </span>
        </label>
      </div>
      <div v-if="props.shownControl" :class="$style.controls">
        <button
          @click="emit('movePrevious')"
          :class="$style.buttonMovePrevious"
        >
          <IconAngle />
        </button>
        <button @click="emit('moveNext')" :class="$style.buttonMoveNext">
          <IconAngle />
        </button>
        <button @click="clickDeleteButton" :class="$style.buttonDelete">
          <IconClose />
        </button>
      </div>
    </header>
    <ChatMessageSendingJobNewPageTextInput
      v-if="message.type === 'text'"
      v-model="message"
      :class="$style.input"
    />
    <ChatMessageSendingJobNewPageImageInput
      v-if="message.type === 'image'"
      v-model="message"
      :class="$style.input"
    />
  </section>
</template>

<style module lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  min-height: 32px;
}

.optionsWrapper {
  display: flex;
  gap: 24px;
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.buttonMovePrevious {
  font-size: 18px;
  line-height: 18px;
  rotate: 270deg;

  svg {
    margin-right: 4px;
  }
}

.buttonMoveNext {
  font-size: 18px;
  line-height: 18px;
  rotate: 90deg;

  svg {
    margin-left: 4px;
  }
}

.buttonDelete {
  font-size: 24px;
  line-height: 24px;

  svg {
    margin-top: 4px;
  }
}

.input {
  margin-top: 16px;
}
</style>
